import { useState, useEffect } from 'react';
import { TrendingUp, Plus, Trash2, Save } from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface VolumeDiscount {
  id?: number;
  product_id: number | null;
  category_id: number | null;
  min_quantity: number;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  product_name?: string;
  category_name?: string;
}

export default function UpsellVolumeDiscounts() {
  const [discounts, setDiscounts] = useState<VolumeDiscount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state for new discount
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDiscount, setNewDiscount] = useState<Partial<VolumeDiscount>>({
    product_id: null,
    category_id: null,
    min_quantity: 5,
    discount_type: 'percentage',
    discount_value: 10,
  });

  useEffect(() => {
    fetchDiscounts();
  }, []);

  async function fetchDiscounts() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ discounts: VolumeDiscount[] }>('/admin/volume-discounts');
      setDiscounts(response.discounts || []);
    } catch (err) {
      setError('Failed to load volume discounts. Please try again.');
      console.error('Error fetching volume discounts:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleAddDiscount(e: React.FormEvent) {
    e.preventDefault();
    setSuccessMessage(null);

    if (!newDiscount.min_quantity || !newDiscount.discount_value) {
      setError('Please fill in all required fields');
      return;
    }

    if (!newDiscount.product_id && !newDiscount.category_id) {
      setError('Please specify either a product ID or category ID');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      await api.post('/admin/volume-discounts', newDiscount);
      setSuccessMessage('Volume discount added successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowAddForm(false);
      setNewDiscount({
        product_id: null,
        category_id: null,
        min_quantity: 5,
        discount_type: 'percentage',
        discount_value: 10,
      });
      await fetchDiscounts();
    } catch (err) {
      setError('Failed to add discount. Please try again.');
      console.error('Error adding discount:', err);
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Are you sure you want to delete this volume discount?')) {
      return;
    }

    try {
      setError(null);
      await api.delete(`/admin/volume-discounts/${id}`);
      setSuccessMessage('Volume discount deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      setDiscounts(prev => prev.filter(d => d.id !== id));
    } catch (err) {
      setError('Failed to delete discount. Please try again.');
      console.error('Error deleting discount:', err);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading volume discounts...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-8 h-8 text-gray-700" aria-hidden="true" />
              <h1 className="text-3xl font-bold text-gray-900">Volume Discounts</h1>
            </div>
            <p className="text-gray-600">Encourage bulk purchases with quantity-based discounts</p>
          </div>
          <Button
            type="button"
            variant="primary"
            size="md"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" />
            Add Discount
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {showAddForm && (
        <Card title="Add New Volume Discount" padding="lg" className="mb-6">
          <form onSubmit={handleAddDiscount} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Product ID (optional)"
                type="number"
                value={newDiscount.product_id || ''}
                onChange={(e) => setNewDiscount(prev => ({
                  ...prev,
                  product_id: e.target.value ? parseInt(e.target.value) : null,
                  category_id: null,
                }))}
                placeholder="123"
                min="1"
                helperText="Leave blank to apply to category"
              />
              <Input
                label="Category ID (optional)"
                type="number"
                value={newDiscount.category_id || ''}
                onChange={(e) => setNewDiscount(prev => ({
                  ...prev,
                  category_id: e.target.value ? parseInt(e.target.value) : null,
                  product_id: null,
                }))}
                placeholder="5"
                min="1"
                helperText="Leave blank to apply to specific product"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Minimum Quantity"
                type="number"
                value={String(newDiscount.min_quantity || '')}
                onChange={(e) => setNewDiscount(prev => ({
                  ...prev,
                  min_quantity: parseInt(e.target.value) || 1,
                }))}
                placeholder="5"
                min="1"
                required
              />
              <div>
                <label htmlFor="discount_type" className="block text-sm font-medium text-stone-700 mb-1">
                  Discount Type
                </label>
                <select
                  id="discount_type"
                  value={newDiscount.discount_type}
                  onChange={(e) => setNewDiscount(prev => ({
                    ...prev,
                    discount_type: e.target.value as 'percentage' | 'fixed',
                  }))}
                  className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
              </div>
              <Input
                label="Discount Value"
                type="number"
                value={String(newDiscount.discount_value || '')}
                onChange={(e) => setNewDiscount(prev => ({
                  ...prev,
                  discount_value: parseFloat(e.target.value) || 0,
                }))}
                placeholder="10"
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="flex gap-3">
              <Button type="submit" variant="primary" size="md" isLoading={isSaving}>
                <Save className="w-4 h-4 mr-2" aria-hidden="true" />
                Save Discount
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="md"
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card title={`Active Volume Discounts (${discounts.length})`} padding="lg">
        {discounts.length === 0 ? (
          <p className="text-stone-500 text-center py-8">
            No volume discounts configured. Click "Add Discount" to create one.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Applies To</th>
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Min Quantity</th>
                  <th className="text-left py-3 px-2 font-medium text-stone-700">Discount</th>
                  <th className="text-right py-3 px-2 font-medium text-stone-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {discounts.map(discount => (
                  <tr key={discount.id} className="border-b border-stone-100">
                    <td className="py-3 px-2 text-stone-900">
                      {discount.product_id ? (
                        <span>Product #{discount.product_id}</span>
                      ) : (
                        <span>Category #{discount.category_id}</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-stone-600">{discount.min_quantity}+</td>
                    <td className="py-3 px-2 text-stone-600">
                      {discount.discount_type === 'percentage'
                        ? `${discount.discount_value}%`
                        : `$${discount.discount_value.toFixed(2)}`}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(discount.id!)}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" aria-hidden="true" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
